import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, User, ArrowRight, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/admin/login', formData);
      const { session_token, admin } = response.data;

      // Set cookie and login
      await login(session_token, admin);
      toast.success('Admin login successful!');
      navigate('/hidden/admin/dashboard');
    } catch (error) {
      console.error('Admin login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-pearl" data-testid="admin-login-page">
      <div className="container-custom py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-md mx-auto"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-3">
              <div className="w-16 h-16 rounded-sm border-2 border-brand-ivory flex items-center justify-center">
                <Lock className="w-8 h-8 text-brand-ivory" />
              </div>
              <div className="text-left">
                <div className="text-2xl font-heading font-bold text-brand-ivory">Admin Panel</div>
                <div className="text-sm text-brand-ivory/80">Rudra Apparel</div>
              </div>
            </div>
          </div>

          {/* Card */}
          <div className="bg-brand-ivory rounded-sm border border-brand-ivory/20 p-8 shadow-2xl">
            <h1 className="text-3xl font-heading font-bold text-brand-pearl mb-2" data-testid="admin-login-heading">
              Secure Access
            </h1>
            <p className="text-brand-walnut mb-6">Enter your credentials to access the admin dashboard</p>

            <form onSubmit={handleSubmit} data-testid="admin-login-form">
              <div className="mb-6">
                <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="username">
                  Username
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-brand-walnut" />
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    placeholder="admin"
                    data-testid="admin-username-input"
                    className="w-full pl-12 pr-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                    required
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-brand-pearl mb-2" htmlFor="password">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-brand-walnut" />
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    data-testid="admin-password-input"
                    className="w-full pl-12 pr-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-btn"
                className="w-full flex items-center justify-center px-6 py-3 bg-brand-pearl text-brand-ivory font-medium rounded-sm hover:bg-brand-walnut transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  <>
                    Access Admin Panel
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <p className="mt-6 text-xs text-center text-brand-walnut">
              This is a secure area. Unauthorized access is prohibited.
            </p>
          </div>

          {/* Default credentials info (remove in production) */}
          <div className="mt-4 text-center text-sm text-brand-ivory/60">
            Default: admin / abc@1234
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminLoginPage;